'use strict';

/**
 * MarkdownRender
 * --------------
 * Renders markdown read-only into a DOM container using the same pieces the
 * editor's live preview uses: pict-section-content's parseMarkdown() for the
 * HTML, then mermaid.run() and katex.render() to activate diagrams and math.
 * mermaid + katex are loaded as globals by html/index.html. Browser-only.
 *
 *   MarkdownRender.renderInto(this.pict.providers.Content, containerEl, markdown)
 */

function _activateMermaid(pContainer)
{
	if (typeof window === 'undefined' || typeof window.mermaid === 'undefined' || !pContainer) { return; }
	let tmpNodes = pContainer.querySelectorAll('pre.mermaid');
	if (!tmpNodes.length) { return; }
	// mermaid.run() consumes the element text; cache the source so a re-render works.
	for (let i = 0; i < tmpNodes.length; i++)
	{
		if (!tmpNodes[i].hasAttribute('data-mermaid-source')) { tmpNodes[i].setAttribute('data-mermaid-source', tmpNodes[i].textContent); }
	}
	try
	{
		let tmpResult = window.mermaid.run({ nodes: tmpNodes });
		if (tmpResult && typeof tmpResult.catch === 'function') { tmpResult.catch(() => { /* diagram errors render inline */ }); }
	}
	catch (pError) { /* mermaid not ready */ }
}

function _activateKaTeX(pContainer)
{
	if (typeof window === 'undefined' || typeof window.katex === 'undefined' || !pContainer) { return; }
	let tmpInline = pContainer.querySelectorAll('.pict-content-katex-inline');
	for (let i = 0; i < tmpInline.length; i++)
	{
		try { window.katex.render(tmpInline[i].textContent, tmpInline[i], { throwOnError: false, displayMode: false }); }
		catch (pError) { /* leave the raw source */ }
	}
	let tmpDisplay = pContainer.querySelectorAll('.pict-content-katex-display');
	for (let i = 0; i < tmpDisplay.length; i++)
	{
		try { window.katex.render(tmpDisplay[i].textContent, tmpDisplay[i], { throwOnError: false, displayMode: true }); }
		catch (pError) { /* leave the raw source */ }
	}
}

// Escape the HTML-significant characters so raw HTML in UNTRUSTED markdown (a node's reply, any externally
// sourced text) cannot inject a tag or break out of a double-quoted attribute. Markdown syntax (* # [] () `) is
// untouched, so links, emphasis and headings still render; only literal & < > " are neutralized. Escape & first.
function _escapeHTML(pText)
{
	return String(pText || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// A link resolver for untrusted content: only http(s) links survive (opening in a new tab); anything else
// (javascript:, data:, a relative path) is defanged to '#'. parseMarkdown inserts the resolved href verbatim,
// so it is handed the href from the already-escaped source.
function _safeLinkResolver(pHref)
{
	if (/^https?:\/\//i.test(String(pHref || ''))) { return { href: String(pHref), target: '_blank', rel: 'noopener noreferrer' }; }
	return { href: '#' };
}

/**
 * Parse pMarkdown to HTML via the Content provider and drop it into pContainerEl
 * (wrapped in .pict-content), then activate diagrams + math.
 *
 * pUntrusted (true for a node reply or any externally sourced markdown): HTML-escape the source before parsing
 * so raw tags cannot execute, and resolve links through a scheme allowlist. The provider's inline-HTML
 * passthrough is kept for trusted callers (the docs library, authored prompts) where pUntrusted is falsy.
 */
function renderInto(pProvider, pContainerEl, pMarkdown, pEmptyHTML, pUntrusted)
{
	if (!pContainerEl) { return; }
	let tmpMarkdown = (typeof pMarkdown === 'string') ? pMarkdown : '';
	if (!tmpMarkdown.trim())
	{
		pContainerEl.innerHTML = pEmptyHTML || '';
		return;
	}
	let tmpSource = pUntrusted ? _escapeHTML(tmpMarkdown) : tmpMarkdown;
	let tmpResolver = pUntrusted ? _safeLinkResolver : null;
	let tmpHTML = '';
	try { tmpHTML = (pProvider && typeof pProvider.parseMarkdown === 'function') ? (pProvider.parseMarkdown(tmpSource, tmpResolver, null, null) || '') : ''; }
	catch (pError) { tmpHTML = ''; }
	pContainerEl.innerHTML = '<div class="pict-content">' + tmpHTML + '</div>';
	let tmpBody = pContainerEl.querySelector('.pict-content');
	_activateMermaid(tmpBody);
	_activateKaTeX(tmpBody);
}

module.exports = { renderInto: renderInto };
